import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppThunk } from "@Store/index";
import axios from "axios";
import getCmsRequestUrl from "@Pages/Blog/utils/getCmsRequestUrl";
import { getMetaSuccess } from "@App/appReducer";
import { getBlogLanguageFromPath } from "@Pages/Blog/utils/blogLanguage";
import getAuthorMeta from "../utils/getAuthorMeta";

export interface AuthorPageState {
  readyStatus: "invalid" | "request" | "success" | "failure";
  contributor: any;
  writtenBlogs: any[];
  reviewedBlogs: any[];
  authoredCount: number;
  reviewedCount: number;
  slug: string;
}

const initialState: AuthorPageState = {
  readyStatus: "invalid",
  contributor: null,
  writtenBlogs: [],
  reviewedBlogs: [],
  authoredCount: 0,
  reviewedCount: 0,
  slug: "",
};

const authorPageSlice = createSlice({
  name: "authorPageData",
  initialState,
  reducers: {
    setRequest: (state) => {
      state.readyStatus = "request";
    },
    setSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        contributor: any;
        writtenBlogs: any[];
        reviewedBlogs: any[];
        authoredCount: number;
        reviewedCount: number;
        slug: string;
      }>,
    ) => {
      state.readyStatus = "success";
      state.contributor = payload.contributor;
      state.writtenBlogs = payload.writtenBlogs;
      state.reviewedBlogs = payload.reviewedBlogs;
      state.authoredCount = payload.authoredCount;
      state.reviewedCount = payload.reviewedCount;
      state.slug = payload.slug;
    },
    setFailure: (state) => {
      state.readyStatus = "failure";
      state.contributor = null;
    },
  },
});

const { setRequest, setSuccess, setFailure } = authorPageSlice.actions;
export default authorPageSlice.reducer;

export const fetchAuthorPageData =
  (props: any): AppThunk =>
  async (dispatch, getState) => {
    const authorSlug =
      props?.params?.slug ||
      (__SERVER__
        ? props?.req?.query?.author
        : new URLSearchParams(window.location.search).get("author"));

    if (!authorSlug) {
      dispatch(setFailure());
      return;
    }

    // Skip if already cached for this slug
    const current = (getState() as any).authorPageData;
    if (current?.slug === authorSlug && current?.readyStatus === "success") {
      return;
    }

    dispatch(setRequest());

    try {
      // Fetch contributor
      const contributorRes = await axios.get(getCmsRequestUrl(`/api/contributors/${authorSlug}`));

      if (!contributorRes.data?.success || !contributorRes.data?.contributor) {
        if (props?.staticContext) props.staticContext.statusCode = 404;
        dispatch(setFailure());
        return;
      }

      const { contributor } = contributorRes.data;
      const requestPath = __SERVER__ ? props?.req?.path || "" : window.location.pathname;
      dispatch(
        getMetaSuccess(
          getAuthorMeta(contributor, authorSlug, getBlogLanguageFromPath(requestPath)),
        ),
      );
      const authoredCount = contributorRes.data.authoredArticlesCount || 0;
      const reviewedCount = contributorRes.data.reviewedArticlesCount || 0;

      // Fetch written and reviewed articles separately
      const [writtenRes, reviewedRes] = await Promise.all([
        axios.get(
          getCmsRequestUrl(
            `/api/blog-articles?where[writer][equals]=${contributor.id}&where[_status][equals]=published&where[isArchived][equals]=false&depth=1&limit=20&sort=-displayDate`,
          ),
        ),
        axios.get(
          getCmsRequestUrl(
            `/api/blog-articles?where[reviewer][equals]=${contributor.id}&where[_status][equals]=published&where[isArchived][equals]=false&depth=1&limit=20&sort=-displayDate`,
          ),
        ),
      ]);

      const writtenBlogs = writtenRes.data?.docs || [];
      const reviewedBlogs = reviewedRes.data?.docs || [];

      dispatch(
        setSuccess({
          contributor,
          writtenBlogs,
          reviewedBlogs,
          authoredCount,
          reviewedCount,
          slug: authorSlug,
        }),
      );
    } catch (error: any) {
      if (props?.staticContext) {
        props.staticContext.statusCode = error?.response?.status === 404 ? 404 : 500;
      }
      dispatch(setFailure());
    }
  };
